import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/api/axiosInstance";
import { setSchool, setUserData } from "./userSlice";

const feeCacheKey = (schoolId, classId) => `${schoolId ?? "unscoped"}::${classId}`;

export const fetchAllFees = createAsyncThunk(
  "fees/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    const schoolId = getState().user?.schoolId ?? null;
    try {
      const response = await axiosInstance.get("/fee/all");
      return { schoolId, fees: response?.data?.data || [] };
    } catch (error) {
      console.error("Error fetching all fees:", error);
      return rejectWithValue({ schoolId, message: error?.response?.data?.message || error.message || "Failed to fetch fees" });
    }
  }
);

export const fetchFeesByClass = createAsyncThunk(
  "fees/fetchByClass",
  async (classId, { getState, rejectWithValue }) => {
    const schoolId = getState().user?.schoolId ?? null;
    const cacheKey = feeCacheKey(schoolId, classId);
    const cachedFees = getState().fees.feesByClass[cacheKey];
    if (cachedFees) return { classId, schoolId, fees: cachedFees };

    try {
      const response = await axiosInstance.get("/fee/all", {
        params: { schoolClass: classId },
      });
      return { classId, schoolId, fees: response?.data?.data || [] };
    } catch (error) {
      console.error("Error fetching fees by class:", error);
      return rejectWithValue({ schoolId, message: error?.response?.data?.message || error.message || "Failed to fetch fees" });
    }
  }
);

const feeSlice = createSlice({
  name: "fees",
  initialState: {
    allFees: [],
    feesByClass: {},
    schoolId: null,
    loadingAll: false,
    loadingByClass: false,
    currentLoadingClass: null,
    error: null,
  },
  reducers: {
    clearFeesForClass(state, action) {
      delete state.feesByClass[feeCacheKey(state.schoolId, action.payload)];
    },
    resetFees(state) {
      state.allFees = [];
      state.feesByClass = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setUserData, (state, action) => {
        const schoolId = action.payload.schoolId ?? null;
        if (state.schoolId !== schoolId) {
          state.allFees = [];
          state.feesByClass = {};
          state.error = null;
          state.loadingAll = false;
          state.loadingByClass = false;
          state.currentLoadingClass = null;
        }
        state.schoolId = schoolId;
      })
      .addCase(setSchool, (state, action) => {
        const schoolId = action.payload.schoolId ?? null;
        if (state.schoolId !== schoolId) {
          state.allFees = [];
          state.feesByClass = {};
          state.error = null;
          state.loadingAll = false;
          state.loadingByClass = false;
          state.currentLoadingClass = null;
        }
        state.schoolId = schoolId;
      })
      .addCase(fetchAllFees.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(fetchAllFees.fulfilled, (state, action) => {
        state.loadingAll = false;
        if (state.schoolId !== action.payload.schoolId) return;
        state.allFees = action.payload.fees;
        const grouped = {};
        action.payload.fees.forEach((fee) => {
          const classId = fee.schoolClass?._id || fee.schoolClass;
          if (!classId) return;
          if (!grouped[classId]) grouped[classId] = [];
          grouped[classId].push(fee);
        });
        Object.entries(grouped).forEach(([classId, fees]) => {
          state.feesByClass[feeCacheKey(state.schoolId, classId)] = fees;
        });
      })
      .addCase(fetchAllFees.rejected, (state, action) => {
        if (action.payload?.schoolId !== state.schoolId) return;
        state.loadingAll = false;
        state.error = action.payload?.message || "Failed to fetch fees";
      })
      .addCase(fetchFeesByClass.pending, (state, action) => {
        state.loadingByClass = true;
        state.currentLoadingClass = action.meta.arg;
        state.error = null;
      })
      .addCase(fetchFeesByClass.fulfilled, (state, action) => {
        state.loadingByClass = false;
        state.currentLoadingClass = null;
        if (state.schoolId !== action.payload.schoolId) return;
        const { classId, schoolId, fees } = action.payload;
        state.feesByClass[feeCacheKey(schoolId, classId)] = fees;
      })
      .addCase(fetchFeesByClass.rejected, (state, action) => {
        if (action.payload?.schoolId !== state.schoolId) return;
        state.loadingByClass = false;
        state.currentLoadingClass = null;
        state.error = action.payload?.message || "Failed to fetch fees";
      });
  },
});

export const { clearFeesForClass, resetFees } = feeSlice.actions;
export default feeSlice.reducer;

export const selectAllFees = (state) => state.fees.allFees;
export const selectFeesByClass = (classId) => (state) =>
  state.fees.feesByClass[feeCacheKey(state.user?.schoolId ?? null, classId)] || [];
export const selectFeesLoadingByClass = (state) => state.fees.loadingByClass;
export const selectFeesLoadingAll = (state) => state.fees.loadingAll;
export const selectFeesError = (state) => state.fees.error;
